import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

interface SearchResult {
  id: number;
  file_path: string;
  title: string;
  section_heading: string | null;
  content_chunk: string;
  similarity: number;
  source_type: string;
}

const baseUrl = API_BASE_URL;

async function fetchSearchResults(query: string): Promise<SearchResult[]> {
  const res = await fetch(
    `${baseUrl}/api/knowledge/search?q=${encodeURIComponent(query)}&limit=8`,
  );
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { ok: boolean; results: SearchResult[] };
  if (!data.ok) throw new Error('Search failed');
  return data.results;
}

export default function SearchBar() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Query
  const { data: results } = useQuery<SearchResult[]>(
    ['knowledgeSearch', debouncedQuery],
    () => fetchSearchResults(debouncedQuery),
    { enabled: debouncedQuery.length >= 2 },
  );

  // Show dropdown when we have results
  useEffect(() => {
    if (results && results.length > 0 && debouncedQuery.length >= 2) {
      setOpen(true);
    }
  }, [results, debouncedQuery]);

  // Click outside to close
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
    if (e.key === 'Enter' && query.trim().length >= 2) {
      setOpen(false);
      navigate(`/knowledge/search?q=${encodeURIComponent(query.trim())}`);
    }
  }

  function snippet(text: string, maxLen = 120): string {
    return text.length > maxLen ? text.slice(0, maxLen) + '...' : text;
  }

  function similarityLabel(score: number): string {
    const pct = Math.round(score * 100);
    return `${pct}%`;
  }

  return (
    <div className="search-bar" ref={wrapperRef}>
      <input
        ref={inputRef}
        type="text"
        className="search-bar__input"
        placeholder="Search knowledge..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results && results.length > 0 && setOpen(true)}
        onKeyDown={handleKeyDown}
      />

      {open && results && results.length > 0 && (
        <div className="search-dropdown">
          {results.map((r) => (
            <button
              key={r.id}
              className="search-result"
              onClick={() => {
                setOpen(false);
                navigate(`/knowledge/search?q=${encodeURIComponent(query.trim())}`);
              }}
            >
              <div className="search-result__header">
                <span className="search-result__title">{r.title}</span>
                <span className="similarity-badge">{similarityLabel(r.similarity)}</span>
              </div>
              {r.section_heading && (
                <span className="search-result__heading">{r.section_heading}</span>
              )}
              <span className="search-result__snippet">{snippet(r.content_chunk)}</span>
            </button>
          ))}
          <button
            className="search-dropdown__see-all"
            onClick={() => {
              setOpen(false);
              navigate(`/knowledge/search?q=${encodeURIComponent(query.trim())}`);
            }}
          >
            See all results &rarr;
          </button>
        </div>
      )}
    </div>
  );
}
